import { format } from 'date-fns';
import {
  TransportTripsReportItemFormattedDTO,
  TripsFilters,
  TripsFiltersState,
  TransportTripsReportItemDTO,
  TransportGroupedTripsReportItemDTO,
  TransportGroupedTripsReportItemFormattedDTO,
  TransportTripsReportTotalsDTO,
  TransportTripsReportTotalsFormattedDTO,
} from '../types';

import { getTimezone } from '../../../../global/utils';
import {
  formatReportPayloadDate,
  sortReportByDate,
} from '../../../utils/helpers';
import { formatAddress, getDuration } from '../../../../shared/utils';
import { SortDirection } from '../../../../shared/utils/constants';
import { formatDistance } from '../../../../shared/utils/format-distance';
import { formatFuelConsumption } from '../../../../shared/utils/format-fuel-consumption';
import { formatTimeRange } from '../../../../shared/utils/format-time-range';
import { formatDate } from '../../../../shared/utils/format-date';

export const formatTripsReportTotal = (
  total: TransportTripsReportTotalsDTO,
  items: TransportTripsReportItemDTO[] | TransportGroupedTripsReportItemDTO[],
  groupByDays: boolean,
): TransportTripsReportTotalsFormattedDTO => {
  const sortedItems = groupByDays
    ? (items as TransportGroupedTripsReportItemDTO[]).flatMap(
        (item) => item.trips,
      )
    : sortReportByDate(
        items as TransportTripsReportItemDTO[],
        'startTime',
        SortDirection.DESC,
      );

  return {
    ...total,
    timeFormatted: sortedItems.length
      ? formatTimeRange(
          sortedItems[sortedItems.length - 1].startTime,
          sortedItems[0].endTime,
        )
      : '-',
    startAddressFormatted: sortedItems.length
      ? formatAddress(sortedItems[sortedItems.length - 1].startAddress)
      : '-',
    endAddressFormatted: sortedItems.length
      ? formatAddress(sortedItems[0].endAddress)
      : '-',
    distanceFormatted: formatDistance(total.distance),
    durationFormatted: getDuration(total.duration),
    fuelConsumptionFormatted: formatFuelConsumption(total.fuelConsumption),
    idlingTimeFormatted: getDuration(total.idlingTime),
    parkingTimeFormatted: getDuration(total.parkingTime),
  };
};

export const formatTripsReportItem = (
  items: TransportTripsReportItemDTO[],
): TransportTripsReportItemFormattedDTO[] => {
  return items.map((item) => ({
    ...item,
    durationFormatted: getDuration(item.duration),
    idlingTimeFormatted: getDuration(item.idlingTime),
    distanceFormatted: formatDistance(item.distance),
    fuelConsumptionFormatted: formatFuelConsumption(item.fuelConsumption),
    startAddressFormatted: formatAddress(item.startAddress),
    endAddressFormatted: formatAddress(item.endAddress),
    dateFormatted: formatDate(item.startTime),
    timeFormatted: formatTimeRange(item.startTime, item.endTime),
  }));
};

export const formatGroupedTripsReportItem = (
  items: TransportGroupedTripsReportItemDTO[],
): TransportGroupedTripsReportItemFormattedDTO[] => {
  return items.map((item) => ({
    ...item,
    ...item.total,
    id: crypto.randomUUID(),
    dateFormatted: formatGroupedTripsDate(item.date),
    distanceFormatted: formatDistance(item.total.distance),
    durationFormatted: getDuration(item.total.duration),
    fuelConsumptionFormatted: formatFuelConsumption(item.total.fuelConsumption),
    idlingTimeFormatted: getDuration(item.total.idlingTime),
    parkingTimeFormatted: getDuration(item.total.parkingTime),
    trips: formatTripsReportItem(item.trips),
  }));
};

export const formatTripsReportPayload = (
  filters: TripsFiltersState,
): TripsFilters => {
  return {
    transports: filters.transports,
    duration: filters.duration * 60,
    minDistance: filters.distanceRange[0] * 1000,
    maxDistance: filters.distanceRange[1] * 1000,
    groupByDays: filters.groupByDays,
    start: formatReportPayloadDate(filters.start),
    end: formatReportPayloadDate(filters.end),
    tz: getTimezone(),
  };
};

const formatGroupedTripsDate = (date: string) => {
  return date ? format(new Date(date), 'dd MMM, yyyy') : '-';
};
